import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import axios from 'axios';
import TestView from './TestView';
import GenerationView from './GenerationView';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

// ScrollToTop Component - Scrolls to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Navigation Bar Component
function NavigationBar() {
  const navigate = useNavigate();
  
  const handleLogoClick = () => {
    navigate('/');
  };

  const handleFeedbackClick = () => {
    window.open('https://eindexamensite.upvotyfeedback.com/', '_blank');
  };

  return (
    <nav className="top-navigation">
      <div className="nav-container">
        <div className="nav-logo" onClick={handleLogoClick}>
          <img src="/logo.png" alt="Eindexamensite" className="logo-image" />
          <div className="ai-badge">AI</div>
        </div>
        <div className="nav-links">
          <button 
            className="feedback-link" 
            onClick={handleFeedbackClick}
            title="Geef feedback en bekijk de roadmap"
          >
            Feedback & Roadmap
          </button>
        </div>
      </div>
    </nav>
  );
}

// HomePage Component
function HomePage() {
  const navigate = useNavigate();
  const [language, setLanguage] = useState('');
  const [level, setLevel] = useState('');
  const [existingTests, setExistingTests] = useState([]);
  const [loadingTests, setLoadingTests] = useState(false);

  const languages = [
    { code: 'nl', name: 'Nederlands' },
    { code: 'en', name: 'English' },
    { code: 'fr', name: 'Français' },
    { code: 'es', name: 'Español' }
  ];

  const levels = [
    'VMBO BB',
    'VMBO KB',
    'VMBO GT',
    'HAVO',
    'VWO'
  ];

  // Validation helpers
  const isLanguageValid = language.trim() !== '';
  const isLevelValid = level.trim() !== '';

  // Fetch existing tests on component mount
  useEffect(() => {
    fetchExistingTests();
  }, []);

  const fetchExistingTests = async () => {
    try {
      setLoadingTests(true);
      const response = await axios.get(`${API_BASE_URL}/tests?limit=20`);
      setExistingTests(response.data.tests || []);
    } catch (error) {
      console.error('Error fetching existing tests:', error);
    } finally {
      setLoadingTests(false);
    }
  };

  const navigateToTest = (testId) => {
    navigate(`/test/${testId}`);
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'completed': 
        return {
          backgroundColor: 'rgba(76, 175, 80, 0.15)',
          color: '#4CAF50'
        };
      case 'failed': 
        return {
          backgroundColor: 'rgba(244, 67, 54, 0.15)',
          color: '#F44336'
        };
      case 'in_progress': 
        return {
          backgroundColor: 'rgba(255, 152, 0, 0.15)',
          color: '#FF9800'
        };
      case 'pending': 
        return {
          backgroundColor: 'rgba(158, 158, 158, 0.15)',
          color: '#9E9E9E'
        };
      default: 
        return {
          backgroundColor: 'rgba(158, 158, 158, 0.15)',
          color: '#9E9E9E'
        };
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return 'Voltooid';
      case 'failed': return 'Mislukt';
      case 'in_progress': return 'Bezig';
      case 'pending': return 'Wachtend';
      default: return 'Onbekend';
    }
  };

  const getLanguageEmoji = (language) => {
    switch (language) {
      case 'nl': return '🇳🇱';
      case 'en': return '🇬🇧';
      case 'fr': return '🇫🇷';
      case 'es': return '🇪🇸';
      default: return '🌐';
    }
  };

  const getRelativeTime = (dateString) => {
    if (!dateString) return 'Onbekend';
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Zojuist';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minuten geleden`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} uur geleden`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} dagen geleden`;
    if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)} maanden geleden`;
    return `${Math.floor(diffInSeconds / 31536000)} jaar geleden`;
  };



  const handleGenerate = () => {
    // Navigate to generation page with language and level
    navigate('/generate', {
      state: {
        language,
        level
      }
    });
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>Taaltoets Generator</h1>
        <p>Genereer taaltoetsen om lees- en luisterbegrip te oefenen</p>
      </header>

      <main className="App-main">
        <div className="input-section">
          <div className="input-row">
            <div className={`input-group ${isLanguageValid ? 'valid' : ''}`}>
              <label htmlFor="language">Taal:</label>
              <div className="input-wrapper">
                <select
                  id="language"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                >
                  <option value="">Selecteer een taal</option>
                  {languages.map(lang => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </select>
                {isLanguageValid && <span className="checkmark">✓</span>}
              </div>
            </div>

            <div className={`input-group ${isLevelValid ? 'valid' : ''}`}>
              <label htmlFor="level">Niveau:</label>
              <div className="input-wrapper">
                <select
                  id="level"
                  value={level}
                  onChange={(e) => setLevel(e.target.value)}
                >
                  <option value="">Selecteer een niveau</option>
                  {levels.map(levelOption => (
                    <option key={levelOption} value={levelOption}>
                      {levelOption}
                    </option>
                  ))}
                </select>
                {isLevelValid && <span className="checkmark">✓</span>}
              </div>
            </div>

            <div className="button-group">
              <button 
                onClick={handleGenerate}
                disabled={!language.trim() || !level.trim()}
                className="generate-button"
              >
                Genereer taaltoets
              </button>
            </div>
          </div>
        </div>

        {/* Existing Tests Section */}
        <div className="existing-tests-section">
          <div className="section-header">
            <h3>Bestaande toetsen</h3>
          </div>
          
          {loadingTests ? (
            <div className="loading-tests">
              <div className="loading-spinner">⏳</div>
              <p>Laden van bestaande tests...</p>
            </div>
          ) : existingTests.length > 0 ? (
            <div className="tests-grid">
              {existingTests.map((test) => (
                <div 
                  key={test.id} 
                  className="test-card"
                  onClick={() => navigateToTest(test.id)}
                >
                  {/* Cover Image */}
                  <div className="test-cover-image">
                    {test.cover_image_url ? (
                      <img 
                        src={test.cover_image_url} 
                        alt={`Cover for ${test.topic}`}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="test-cover-placeholder" style={{ display: test.cover_image_url ? 'none' : 'flex' }}>
                      <div className="placeholder-content">
                        <div className="placeholder-icon">🎬</div>
                        <div className="placeholder-text">Video Test</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="test-card-header">
                    <h4 className="test-topic">{test.topic}</h4>
                    <div className="test-meta">
                      <span className="test-language">{getLanguageEmoji(test.language)}</span>
                      <span className="test-time">{getRelativeTime(test.created_at)}</span>
                    </div>
                    <div 
                      className="test-status"
                      style={getStatusStyle(test.status)}
                    >
                      {getStatusText(test.status)}
                    </div>
                  </div>
                  
                  <div className="test-card-body">
                    {test.error_message && (
                      <div className="error-message">
                        <strong>Fout:</strong> {test.error_message}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-tests">
              <p>Geen bestaande tests gevonden.</p>
              <p>Genereer je eerste luistertoets om te beginnen!</p>
            </div>
          )}
        </div>


      </main>
    </div>
  );
}

// Main App Component with Routing
function App() {
  return (
    <Router>
      <ScrollToTop />
      <div className="app-container">
        <NavigationBar />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/generate" element={<GenerationView />} />
          <Route path="/generate/:testId" element={<GenerationView />} />
          <Route path="/test/:testId" element={<TestView />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
